export type VoucherDataRow = Record<string, string>;

type Scene = Record<string, unknown>;
type TextRecord = Record<string, unknown>;
type VoucherText = {
  title: unknown;
  subtitle: unknown;
  badge: unknown;
  date: unknown;
  link: unknown;
};

const ROW_KEYS = {
  title: ["title"],
  subtitle: ["subtitle", "sub_title"],
  badge: ["badge", "code", "voucher", "promo", "promocode"],
  date: ["date", "redeem_before", "redeem before", "valid_until", "valid till", "valid until"],
  link: ["link", "url", "website"],
} as const;

const SCENE_FIELDS = {
  title: "titles",
  subtitle: "subtitles",
  badge: "badges",
  date: "dates",
  link: "links",
} as const;

function selectRowValue(
  row: VoucherDataRow | null | undefined,
  keys: readonly string[],
  fallback: unknown
): unknown {
  if (!row) return fallback;
  for (const key of keys) {
    const value = row[key];
    if (value === undefined || value === null) continue;
    const trimmed = String(value).trim();
    if (trimmed !== "") return trimmed;
  }
  return fallback;
}

/** Resolve one Voucher data record with the same aliases as the editor preview. */
export function resolveVoucherDataRow(
  row: VoucherDataRow | null | undefined,
  fallback: VoucherText
): VoucherText {
  return {
    title: selectRowValue(row, ROW_KEYS.title, fallback.title),
    subtitle: selectRowValue(row, ROW_KEYS.subtitle, fallback.subtitle),
    badge: selectRowValue(row, ROW_KEYS.badge, fallback.badge),
    date: selectRowValue(row, ROW_KEYS.date, fallback.date),
    link: selectRowValue(row, ROW_KEYS.link, fallback.link),
  };
}

function requireTextRecord(scene: Scene, field: string): TextRecord {
  const value = scene[field];
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Voucher export requires scene.${field} to be an object`);
  }
  return value as TextRecord;
}

/** Convert one Voucher data record into normal scene text fields before rendering. */
export function materializeVoucherDataRow(
  scene: Scene,
  row: VoucherDataRow,
  templateId = "Voucher"
): Scene {
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    throw new Error("Voucher export requires a data record object");
  }

  const records = Object.fromEntries(
    Object.values(SCENE_FIELDS).map((field) => [field, requireTextRecord(scene, field)])
  ) as Record<(typeof SCENE_FIELDS)[keyof typeof SCENE_FIELDS], TextRecord>;
  const resolved = resolveVoucherDataRow(row, {
    title: records.titles[templateId] || records.titles.main,
    subtitle: records.subtitles[templateId] || records.subtitles.main,
    badge: records.badges[templateId] || records.badges.main,
    date: records.dates[templateId] || records.dates.main,
    link: records.links[templateId] || records.links.main,
  });

  const result: Scene = {...scene};
  for (const [key, field] of Object.entries(SCENE_FIELDS) as Array<
    [keyof VoucherText, (typeof SCENE_FIELDS)[keyof typeof SCENE_FIELDS]]
  >) {
    const value = resolved[key];
    result[field] = value === undefined ? records[field] : {...records[field], [templateId]: value};
  }
  return result;
}
